import { useState, useEffect, useRef } from 'react';
import { api, fetchCached } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import SectionWatermark from '@/components/SectionWatermark';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Send, CheckCircle } from 'lucide-react';
import { FALLBACK_CONTACT_INFO } from '@/lib/publicPageData';

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return inView;
}

function AnimatedSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  return (
    <div ref={ref} className={className}
      style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(24px)', transition: `all 0.7s ease-out ${delay}ms` }}>
      {children}
    </div>
  );
}

export default function ContactPage() {
  const [contactInfo, setContactInfo] = useState(FALLBACK_CONTACT_INFO);
  const [form, setForm] = useState({ name: '', email: '', message: '', website: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    fetchCached('/content/contact_info').then(setContactInfo).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all fields');
      return;
    }
    setSubmitError('');
    setSending(true);
    try {
      await api.post('/contact', form);
      setSent(true);
      toast.success('Message sent successfully!');
      setForm({ name: '', email: '', message: '', website: '' });
    } catch (error) {
      const message = error?.response?.data?.detail || 'Failed to send message. Please try again.';
      setSubmitError(message);
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20 mscm-public-shell">
      {/* Hero */}
      <section className="mscm-hero-panel mscm-cinematic-hero py-20 md:py-28">
        <div className="mscm-hero-aurora" />
        <div className="mscm-star-field" />
        <div className="mscm-luminous-sweep" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-8 text-center">
          <span className="text-white/85 text-xs font-bold uppercase tracking-[0.16em]">Connect with us</span>
          <h1 className="mscm-hero-title mt-4 font-bold text-white lg:text-6xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Contact Us
          </h1>
          <p className="mt-4 text-white/80 text-base md:text-lg">
            We'd love to hear from you. Reach out and connect with our community.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <SectionWatermark
        className="mscm-section bg-white"
        contentClassName="max-w-7xl mx-auto px-6 md:px-8"
        watermarkClassName="mscm-watermark-strong"
        opacity={0.18}
      >
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
          {/* Form */}
          <AnimatedSection>
            <div className="mscm-premium-card mscm-top-rule p-5 sm:p-8 md:p-10 h-full">
              <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Send Us a Message
              </h2>

              {sent ? (
                <div data-testid="contact-success" className="bg-[#EAF4FF] rounded-[8px] p-5 text-center sm:p-8">
                  <CheckCircle size={48} className="mx-auto text-[#10B981] mb-4" />
                  <h3 className="text-xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Thank You!
                  </h3>
                  <p className="text-[#475569]">Your message has been sent. We'll get back to you soon.</p>
                  <Button
                    data-testid="contact-send-another"
                    onClick={() => setSent(false)}
                    className="mt-4 min-h-11 w-full sm:w-auto"
                    size="lg"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                  <input
                    type="text"
                    name="website"
                    autoComplete="off"
                    tabIndex={-1}
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    className="hidden"
                    aria-hidden="true"
                  />
                  {submitError ? (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {submitError}
                    </div>
                  ) : null}
                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Name</label>
                    <Input
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your full name"
                      maxLength={120}
                      className="rounded-[8px] border-[#DDEBFA] focus:border-[#1E4FA1] h-12 bg-white/80"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Email</label>
                    <Input
                      data-testid="contact-email-input"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="your@email.com"
                      maxLength={254}
                      className="rounded-[8px] border-[#DDEBFA] focus:border-[#1E4FA1] h-12 bg-white/80"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Message</label>
                    <Textarea
                      data-testid="contact-message-input"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="How can we help you?"
                      rows={5}
                      maxLength={3000}
                      className="rounded-[8px] border-[#DDEBFA] focus:border-[#1E4FA1] bg-white/80"
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={sending}
                    size="lg"
                    className="w-full sm:w-auto"
                  >
                    {sending ? 'Sending...' : (
                      <span className="flex items-center gap-2">
                        Send Message <Send size={16} />
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </AnimatedSection>

          {/* Contact Info */}
          <AnimatedSection delay={150}>
            <div className="flex flex-col gap-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Get in Touch
                </h2>

                <SectionWatermark
                  as="div"
                  className="mb-10"
                  contentClassName="relative"
                  opacity={0.14}
                  watermarkClassName="h-[min(100%,12rem)] w-[min(100%,12rem)] md:h-[min(100%,15rem)] md:w-[min(100%,15rem)] lg:h-[min(100%,16rem)] lg:w-[min(100%,16rem)]"
                >
                  <div className="space-y-6">
                    {contactInfo?.phone && (
                      <div className="flex min-w-0 items-start gap-4" data-testid="contact-phone">
                        <div className="w-12 h-12 bg-[#EAF4FF] rounded-[8px] flex items-center justify-center flex-shrink-0">
                          <Phone size={20} className="text-[#1E4FA1]" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm text-[#94A3B8] mb-0.5">Phone</p>
                          <p className="mscm-text-safe text-[#0F172A] font-medium">{contactInfo.phone}</p>
                        </div>
                      </div>
                    )}
                    {contactInfo?.email && (
                      <div className="flex min-w-0 items-start gap-4" data-testid="contact-email">
                        <div className="w-12 h-12 bg-[#EAF4FF] rounded-[8px] flex items-center justify-center flex-shrink-0">
                          <Mail size={20} className="text-[#1E4FA1]" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm text-[#94A3B8] mb-0.5">Email</p>
                          <a href={`mailto:${contactInfo.email}`} className="mscm-text-safe inline-block max-w-full text-[#1E4FA1] font-medium hover:underline">{contactInfo.email}</a>
                        </div>
                      </div>
                    )}
                    {contactInfo?.address && (
                      <div className="flex min-w-0 items-start gap-4" data-testid="contact-address">
                        <div className="w-12 h-12 bg-[#EAF4FF] rounded-[8px] flex items-center justify-center flex-shrink-0">
                          <MapPin size={20} className="text-[#1E4FA1]" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm text-[#94A3B8] mb-0.5">Address</p>
                          <p className="mscm-text-safe text-[#0F172A] font-medium">{contactInfo.address}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </SectionWatermark>
              </div>

              {/* Map */}
              {contactInfo?.map_embed_url ? (
                <div data-testid="contact-map" className="rounded-[8px] overflow-hidden border border-[#DDEBFA] shadow-[0_18px_48px_rgba(11,31,77,0.08)] h-[300px]">
                  <iframe
                    src={contactInfo.map_embed_url}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Location Map"
                  />
                </div>
              ) : (
                <div className="rounded-[8px] bg-[#F8FBFF] border border-[#DDEBFA] h-[300px] flex items-center justify-center">
                  <div className="text-center">
                    <MapPin size={32} className="mx-auto text-[#94A3B8] mb-2" />
                    <p className="text-[#94A3B8] text-sm">Please contact us for location details.</p>
                  </div>
                </div>
              )}
            </div>
          </AnimatedSection>
        </div>
      </SectionWatermark>
    </div>
  );
}
